import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { profileApi, type Favorite } from '@/services/api';
import { formatFieldValue } from '@/components/feature/GreenCertFieldConfig';

export default function Favorites() {
  const [list, setList] = useState<Favorite[]>([]);
  const [filter, setFilter] = useState<'all' | 'supply' | 'demand'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    profileApi
      .favorites()
      .then((data) => {
        setList(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载失败');
        setLoading(false);
      });
  }, []);

  const removeFavorite = async (id: number) => {
    try {
      await profileApi.removeFavorite(id);
      setList((prev) => prev.filter((f) => f.id !== id));
    } catch {
      // silently fail
    }
  };

  const filtered = filter === 'all' ? list : list.filter((f) => f.type === filter);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
        <p className="text-foreground-400 text-sm mt-3">加载中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-foreground-500 text-sm">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 text-sm text-primary-600 hover:text-primary-700 cursor-pointer font-medium"
        >
          重新加载
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h3 className="font-heading text-lg font-bold text-foreground-900">
          收藏夹 ({list.length})
        </h3>
        <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              filter === 'all' ? 'bg-primary-500 text-background-50' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            全部
          </button>
          <button
            onClick={() => setFilter('supply')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              filter === 'supply' ? 'bg-primary-500 text-background-50' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            供给
          </button>
          <button
            onClick={() => setFilter('demand')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              filter === 'demand' ? 'bg-primary-500 text-background-50' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            需求
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-foreground-400 text-sm py-12 text-center">暂无收藏信息</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((item: Favorite) => {
            const projectLabel = formatFieldValue('projectType', item.projectType);
            return (
              <div
                key={item.id}
                className="bg-background-50 border border-background-200 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-primary-300 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                      item.type === 'supply'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-accent-100 text-accent-700'
                    }`}>
                      {item.type === 'supply' ? '供给' : '需求'}
                    </span>
                    <span className="text-foreground-900 text-sm font-semibold">{item.company}</span>
                    <span className="text-foreground-400 text-xs">| {item.region}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-foreground-400">
                    <span>{projectLabel}</span>
                    <span>{item.year}年度</span>
                    <span>{item.quantity.toLocaleString()} 个</span>
                    <span>&yen;{item.price}/个</span>
                    <span>收藏于 {item.savedDate}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Link
                    to={`/${item.type === 'supply' ? 'supply' : 'demand'}/${item.itemId}`}
                    className="px-3 py-1.5 bg-primary-500 text-background-50 text-xs rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看详情
                  </Link>
                  <button
                    onClick={() => removeFavorite(item.id)}
                    className="px-3 py-1.5 border border-background-300 text-foreground-400 text-xs rounded-md hover:text-accent-600 hover:border-accent-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    取消收藏
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}